/**
 * usePackageAdmin — a `package/02-admin` read-view hook (BE-S1 seam).
 * A `getPackageAdmin` runtime-validál a `PackageAdminResponseSchema`-ra.
 */
import { getPackageAdmin } from '@/api/package-admin';
import type { PackageAdmin } from '@kgc/packages/package-admin';
import { useCallback, useEffect, useState } from 'react';

interface UsePackageAdminResult {
  pkg: PackageAdmin | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePackageAdmin(id: string | undefined): UsePackageAdminResult {
  const [pkg, setPkg] = useState<PackageAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackage = useCallback(async () => {
    if (!id) {
      setPkg(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPackageAdmin(id);
      setPkg(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch package';
      setError(message);
      console.error('Error fetching package admin:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchPackage();
  }, [fetchPackage]);

  return { pkg, isLoading, error, refetch: fetchPackage };
}
